/**
 * Client tool: get_browser_info
 *
 * Returns information about the user's browser and screen resolution.
 *
 * Params JSON Schema:
 * {
 *   "$schema": "http://json-schema.org/draft-07/schema#",
 *   "title": "GetBrowserInfo Tool Params",
 *   "name": "get_browser_info",
 *   "description": "Returns information about the user's browser (name, version) and screen resolution (width, height)",
 *   "parameters": {
 *     "type": "object",
 *     "properties": {},
 *     "additionalProperties": false
 *   }
 * }
 *
 * Returns JSON Schema:
 * {
 *   "$schema": "http://json-schema.org/draft-07/schema#",
 *   "title": "GetBrowserInfo Tool Result",
 *   "type": "object",
 *   "properties": {
 *     "browser": {
 *       "type": "object",
 *       "properties": {
 *         "name": { "type": "string" },
 *         "version": { "type": "string" }
 *       },
 *       "required": ["name", "version"],
 *       "additionalProperties": false
 *     },
 *     "screen": {
 *       "type": "object",
 *       "properties": {
 *         "width": { "type": "number" },
 *         "height": { "type": "number" }
 *       },
 *       "required": ["width", "height"],
 *       "additionalProperties": false
 *     },
 *     "viewport": {
 *       "type": "object",
 *       "properties": {
 *         "width": { "type": "number" },
 *         "height": { "type": "number" }
 *       },
 *       "required": ["width", "height"],
 *       "additionalProperties": false
 *     }
 *   },
 *   "required": ["browser", "screen", "viewport"],
 *   "additionalProperties": false
 * }
 */

interface BrowserInfo {
  name: string;
  version: string;
}

interface ScreenInfo {
  width: number;
  height: number;
}

interface ViewportInfo {
  width: number;
  height: number;
}

interface BrowserInfoResult {
  browser: BrowserInfo;
  screen: ScreenInfo;
  viewport: ViewportInfo;
}

/**
 * Detects browser name and version from user agent string
 */
function detectBrowser(): BrowserInfo {
  const userAgent = globalThis?.navigator?.userAgent || '';

  // Chrome (including Edge Chromium)
  const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
  if (chromeMatch && !userAgent.match(/Edg/)) {
    return {
      name: 'Chrome',
      version: chromeMatch[1]
    };
  }

  // Edge (Chromium-based)
  const edgeMatch = userAgent.match(/Edg\/(\d+)/);
  if (edgeMatch) {
    return {
      name: 'Edge',
      version: edgeMatch[1]
    };
  }

  // Firefox
  const firefoxMatch = userAgent.match(/Firefox\/(\d+)/);
  if (firefoxMatch) {
    return {
      name: 'Firefox',
      version: firefoxMatch[1]
    };
  }

  // Safari
  const safariMatch = userAgent.match(/Version\/(\d+).*Safari/);
  if (safariMatch && !userAgent.match(/Chrome/)) {
    return {
      name: 'Safari',
      version: safariMatch[1]
    };
  }

  // Opera
  const operaMatch = userAgent.match(/OPR\/(\d+)/);
  if (operaMatch) {
    return {
      name: 'Opera',
      version: operaMatch[1]
    };
  }

  // Fallback
  return {
    name: userAgent,
    version: 'Unknown'
  };
}

/**
 * Gets screen resolution
 */
function getScreenInfo(): ScreenInfo {
  return {
    width: globalThis?.screen?.width || 0,
    height: globalThis?.screen?.height || 0
  };
}

/**
 * Gets viewport size (visible area)
 */
function getViewportInfo(): ViewportInfo {
  return {
    width: globalThis?.window?.innerWidth || globalThis?.document?.documentElement?.clientWidth || 0,
    height: globalThis?.window?.innerHeight || globalThis?.document?.documentElement?.clientHeight || 0
  };
}

export const getBrowserInfo = async (): Promise<BrowserInfoResult> => {
  return {
    browser: detectBrowser(),
    screen: getScreenInfo(),
    viewport: getViewportInfo()
  };
};
